import React from 'react';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaCar, FaMapMarkerAlt, FaUser } from 'react-icons/fa';

const UpcomingPickups = ({ rentals }) => {
  // Get the current date
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // Find pending/confirmed rentals with future pickup dates
  const upcomingPickups = rentals
    .filter(rental => {
      const pickupDate = new Date(rental.pickupDateTime);
      
      // Include rentals where pickup is today or in the future
      // Only show pending or confirmed rentals
      return (rental.status === 'pending' || rental.status === 'confirmed') && 
             // Compare only date parts for same-day pickups
             (pickupDate.setHours(0, 0, 0, 0) >= today.getTime());
    })
    .sort((a, b) => new Date(a.pickupDateTime) - new Date(b.pickupDateTime))
    .slice(0, 5); // Show top 5 upcoming pickups
  
  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
    // Calculate days from today
  const getDaysFromToday = (dateString) => {
    const date = new Date(dateString);
    
    // Set both dates to start of day for accurate day comparison
    const pickupDate = new Date(date);
    pickupDate.setHours(0, 0, 0, 0);
    
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    
    // Calculate the difference in days
    const diffTime = pickupDate - todayStart;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return `In ${diffDays} days`;
  };
    // Get color based on how soon the pickup is
  const getPickupColor = (dateString) => {
    const date = new Date(dateString);
    
    // Set both dates to start of day for accurate day comparison
    const pickupDate = new Date(date);
    pickupDate.setHours(0, 0, 0, 0);
    
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    
    // Calculate the difference in days
    const diffTime = pickupDate - todayStart;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 2) return 'text-red-600 bg-red-50';
    if (diffDays < 5) return 'text-orange-600 bg-orange-50';
    if (diffDays < 10) return 'text-amber-600 bg-amber-50';
    return 'text-green-600 bg-green-50';
  };
  
  return (
    <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
      {upcomingPickups.length > 0 ? (
        upcomingPickups.map((rental, index) => (
          <motion.div
            key={rental._id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            className="flex items-center p-3 rounded-lg border border-gray-100 hover:shadow-md transition-all cursor-pointer"
            whileHover={{ scale: 1.02 }}
          >
            <div className={`flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full ${getPickupColor(rental.pickupDateTime)}`}>
              <FaCar size={16} />
            </div>
            <div className="ml-3 flex-grow">              <div className="flex justify-between items-start">
                <p className="font-medium text-sm text-gray-900">
                  {rental.carName}
                </p>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPickupColor(rental.pickupDateTime)}`}>
                  {getDaysFromToday(rental.pickupDateTime)}
                </span>
              </div>
              {rental.vehicleDetails?.licensePlate && (
                <div className="mt-1 text-xs text-gray-600 flex items-center">
                  <FaCar className="mr-1" /> 
                  Plate: {rental.vehicleDetails.licensePlate}
                </div>
              )}
              <div className="mt-1 text-xs text-gray-500 flex items-center">
                <FaUser className="mr-1" /> 
                {rental.customer.firstName} {rental.customer.lastName}
              </div>
              <div className="mt-1 text-xs text-gray-500 flex items-center">
                <FaCalendarAlt className="mr-1" /> 
                {formatDate(rental.pickupDateTime)}
              </div>
              <div className="mt-1 text-xs text-gray-500 flex items-center">
                <FaMapMarkerAlt className="mr-1" /> 
                {rental.pickupLocation}
              </div>
            </div>
          </motion.div>
        ))
      ) : (
        <div className="text-center py-8 text-gray-500">
          <FaCalendarAlt className="mx-auto text-gray-300 mb-2" size={24} />
          <p>No upcoming pickups</p>
        </div>
      )}
    </div>
  );
};

export default UpcomingPickups;
